// Workspace triage page — three sections grouped by live activity,
// with optional groupBy modes (state/epic/assignee/priority/story/none).
// Right-hand rails: ReadyToTestRail + AgentPulseRail (live presence).
//
// Layer 1: TriageCard — full card with state/priority pills, assignee avatar,
//          parent breadcrumb, labels, last-activity, active indicator.
// Layer 2: Progress bar per card (state mapping + child-task ratio).
// Layer 3: GroupByControl segmented-control + keyboard shortcuts 1–6.
// Layer 4: AgentPulseRail — live presence rail with pulsing dots.
// Layer 5: Keyboard nav ↑/↓/←/→/Enter/Esc + multi-select + quick-create.
// Layer 6: Density toggle (Compact / Comfortable).
//
// Reads:
//   s.root.triage              { now, ongoing, upcoming, readyToTest, agentPresence }
//   s.root.filterAssignee      null | 'team' | 'agents' | 'me'
//   s.root.filterEpic          null | epicKey
//   s.root.generalGroupBy      'state'|'epic'|'assignee'|'priority'|'story'|'none'
//   s.root.densityMode         'comfortable' | 'compact'   (AC12)
//   s.root.selectedTicketIds   Array of selected ticket ids  (AC6 — stored as Array, Set used only locally)
//   s.root.kbFocusBucket       null | 'now'|'ongoing'|'upcoming'|'pendingApproval'
//   s.root.kbFocusIndex        number (index within bucket)

import { getSDK } from '../../../shared/functions/sdk.js'
import { formatStateLabel, TICKET_STATE_COLOR } from '../../functions/tickets/ticketUtils.js'
import { ticketStore } from '../../functions/tickets/ticketStore.js'

// ─── Shared helpers ───────────────────────────────────────────────────────────

const _isAgentEmail = (e) => /-agent@symbols\.app$/.test(e || '')
const _isHumanApprovalNeeded = (t) => !!(t && (t.labels || []).includes('human-approval-needed'))

const _approveTicket = async (ticketId) => {
  try {
    await getSDK().execute('workspaceProject.tickets', 'update', {
      id: ticketId,
      payload: { state: 'in_progress', updated_at: new Date().toISOString() }
    })
  } catch (err) { console.error('[approveTicket]', err) }
}

const _actorLabel = (t) => {
  if (t.liveAgent) return t.liveAgent
  if (_isAgentEmail(t.assignee_email)) return (t.assignee_email || '').split('-agent')[0].toUpperCase()
  if (t.assignee_email) return t.assignee_email.split('@')[0]
  return 'unassigned'
}

const _applyAssigneeFilter = (rows, filter, me) => {
  if (!filter) return rows
  const meLC = (me || '').toLowerCase()
  return rows.filter((t) => {
    const e = (t.assignee_email || '').toLowerCase()
    if (filter === 'me') return meLC && e === meLC
    if (filter === 'agents') return _isAgentEmail(e)
    if (filter === 'team') return e && !_isAgentEmail(e) && e !== meLC
    return true
  })
}

const _applyEpicFilter = (rows, epicKey) => {
  if (!epicKey) return rows
  const wanted = String(epicKey).toLowerCase()
  return rows.filter((t) => {
    const title = (t.parentEpicTitle || '').toLowerCase()
    return title === wanted || title.startsWith(wanted)
  })
}

const _sectionRows = (s, key) => {
  const raw = (s.root.triage && s.root.triage[key]) || []
  const byAssignee = _applyAssigneeFilter(raw, s.root.filterAssignee, s.root.me?.email)
  return _applyEpicFilter(byAssignee, s.root.filterEpic)
}

const _blockedRows = (s) => {
  const raw = (s.root.triage && s.root.triage.blocked) || []
  const byAssignee = _applyAssigneeFilter(raw, s.root.filterAssignee, s.root.me?.email)
  return _applyEpicFilter(byAssignee, s.root.filterEpic)
}

const _pendingApprovalRows = (s) => {
  const raw = (s.root.triage && s.root.triage.pendingApproval) || []
  const byAssignee = _applyAssigneeFilter(raw, s.root.filterAssignee, s.root.me?.email)
  return _applyEpicFilter(byAssignee, s.root.filterEpic)
}

const _decisionRows = (s) => {
  const raw = (s.root.triage && s.root.triage.decisions) || []
  const byAssignee = _applyAssigneeFilter(raw, s.root.filterAssignee, s.root.me?.email)
  return _applyEpicFilter(byAssignee, s.root.filterEpic)
}

const _readyToTestRows = (s) => {
  const raw = (s.root.triage && s.root.triage.readyToTest) || []
  const byAssignee = _applyAssigneeFilter(raw, s.root.filterAssignee, s.root.me?.email)
  return _applyEpicFilter(byAssignee, s.root.filterEpic)
}

// Collect all triage rows across buckets for flat groupBy view.
const _allRows = (s) => {
  const all = []
  for (const k of ['now', 'ongoing', 'blocked', 'upcoming']) {
    for (const r of _sectionRows(s, k)) all.push({ ...r, _bucket: k })
  }
  return all
}

// ─── Grouping logic ───────────────────────────────────────────────────────────

const _groupByAssignee = (rows) => {
  const groups = new Map()
  for (const t of rows) {
    const key = (t.liveAgent && `live:${t.liveAgent}`) || (t.assignee_email || 'unassigned')
    if (!groups.has(key)) {
      groups.set(key, {
        groupKey: key,
        actor: _actorLabel(t),
        groupLabel: _actorLabel(t),
        isLive: !!t.liveAgent,
        isAgent: _isAgentEmail(t.assignee_email),
        rows: []
      })
    }
    groups.get(key).rows.push(t)
  }
  return Array.from(groups.values())
}

const PRIORITY_ORDER = { P0: 0, P1: 1, P2: 2, P3: 3 }

const _buildFlatGroups = (rows, groupBy) => {
  if (!groupBy || groupBy === 'assignee') return _groupByAssignee(rows)
  const groups = new Map()
  for (const t of rows) {
    let key, label
    if (groupBy === 'epic') {
      key = t.parentEpicTitle || 'unassigned-epic'
      label = t.parentEpicTitle || 'Unassigned to epic'
    } else if (groupBy === 'story') {
      key = t.parentStoryTitle || 'unassigned-story'
      label = t.parentStoryTitle || 'Unassigned to story'
    } else if (groupBy === 'priority') {
      key = t.priority || 'none'
      label = t.priority || 'No priority'
    } else {
      // 'none' — single flat group
      key = '__all__'
      label = 'All tickets'
    }
    if (!groups.has(key)) {
      groups.set(key, { groupKey: key, groupLabel: label, actor: label,
        isLive: false, isAgent: false, rows: [] })
    }
    groups.get(key).rows.push(t)
  }
  const arr = Array.from(groups.values())
  if (groupBy === 'priority') {
    arr.sort((a, b) => (PRIORITY_ORDER[a.groupKey] ?? 9) - (PRIORITY_ORDER[b.groupKey] ?? 9))
  }
  return arr
}

// ─── Agent pulse helpers ──────────────────────────────────────────────────────

const PULSE_ACTIVE_MS = 5 * 60 * 1000   // < 5 min = pulsing
const PULSE_IDLE_MS = 60 * 60 * 1000    // 5–60 min = idle; > 60 min = stale

const _agentAgeMs = (p) => {
  if (!p.last_heartbeat) return Infinity
  return Date.now() - new Date(p.last_heartbeat).getTime()
}

const _pulsingAgents = (s) =>
  ((s.root.triage && s.root.triage.agentPresence) || [])
    .filter(p => _agentAgeMs(p) < PULSE_ACTIVE_MS)

const _idleAgents = (s) =>
  ((s.root.triage && s.root.triage.agentPresence) || [])
    .filter(p => { const a = _agentAgeMs(p); return a >= PULSE_ACTIVE_MS && a < PULSE_IDLE_MS })

const _staleAgents = (s) =>
  ((s.root.triage && s.root.triage.agentPresence) || [])
    .filter(p => _agentAgeMs(p) >= PULSE_IDLE_MS)

const _relativeTime = (isoStr) => {
  if (!isoStr) return ''
  const ms = Date.now() - new Date(isoStr).getTime()
  const sec = Math.floor(ms / 1000)
  if (sec < 10) return 'just now'
  if (sec < 60) return `${sec}s ago`
  const m = Math.floor(sec / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

// ─── Group-by options ─────────────────────────────────────────────────────────

export const GROUP_BY_OPTIONS = [
  { groupKey: 'state',    label: 'State' },
  { groupKey: 'epic',     label: 'Epic' },
  { groupKey: 'assignee', label: 'Assignee' },
  { groupKey: 'priority', label: 'Priority' },
  { groupKey: 'story',    label: 'Story' },
  { groupKey: 'none',     label: 'None' }
]

const _setGroupBy = (root, key) => {
  root.update({ generalGroupBy: key })
  if (typeof window !== 'undefined') {
    const u = new URL(window.location)
    u.searchParams.set('groupBy', key)
    window.history.replaceState(null, '', u)
  }
}

// ─── Skeleton card (shown while loading) ─────────────────────────────────────
//
// Mimics the TriageCard shape without real data.
// 150ms fade-in so it doesn't flash on fast connections.

export const TriageSkeleton = {
  flow: 'y',
  gap: 'Y',
  padding: 'Z A',
  background: 'overlay',
  round: 'Z1',
  borderWidth: 'V',
  borderStyle: 'solid',
  borderColor: 'line',
  opacity: '0',
  animation: 'triageFadeIn 0.15s ease forwards',

  onInit: (el) => {
    if (typeof document === 'undefined') return
    if (document.querySelector('style[data-triage-skeleton-kf]')) return
    const st = document.createElement('style')
    st.setAttribute('data-triage-skeleton-kf', '')
    st.textContent = [
      '@keyframes triageFadeIn { from { opacity: 0 } to { opacity: 1 } }',
      '@keyframes skeletonShimmer {',
      '  0% { background-position: -200% 0 }',
      '  100% { background-position: 200% 0 }',
      '}'
    ].join('\n')
    document.head.appendChild(st)
  },

  TopRow: {
    flow: 'x',
    gap: 'Z',
    align: 'center left',
    // Pill widths as %/em values — skeleton proportions, not absolute dimensions
    Pill_1: {
      width: '3.5em', height: 'Z', round: 'C',
      background: 'line',
      animation: 'skeletonShimmer 1.5s linear infinite',
      backgroundSize: '200% 100%',
      backgroundImage: 'linear-gradient(90deg, var(--color-line) 25%, var(--color-overlay) 50%, var(--color-line) 75%)'
    },
    Pill_2: {
      width: '2.5em', height: 'Z', round: 'C',
      background: 'line',
      animation: 'skeletonShimmer 1.5s linear infinite 0.2s',
      backgroundSize: '200% 100%',
      backgroundImage: 'linear-gradient(90deg, var(--color-line) 25%, var(--color-overlay) 50%, var(--color-line) 75%)'
    }
  },
  TitleLine: {
    height: 'Z1', round: 'Y2', width: '80%',
    background: 'line',
    animation: 'skeletonShimmer 1.5s linear infinite 0.1s',
    backgroundSize: '200% 100%',
    backgroundImage: 'linear-gradient(90deg, var(--color-line) 25%, var(--color-overlay) 50%, var(--color-line) 75%)'
  },
  TitleLine2: {
    height: 'Z1', round: 'Y2', width: '55%',
    background: 'line',
    animation: 'skeletonShimmer 1.5s linear infinite 0.15s',
    backgroundSize: '200% 100%',
    backgroundImage: 'linear-gradient(90deg, var(--color-line) 25%, var(--color-overlay) 50%, var(--color-line) 75%)'
  },
  MetaLine: {
    height: 'Y1', round: 'Y2', width: '40%',
    background: 'line',
    animation: 'skeletonShimmer 1.5s linear infinite 0.25s',
    backgroundSize: '200% 100%',
    backgroundImage: 'linear-gradient(90deg, var(--color-line) 25%, var(--color-overlay) 50%, var(--color-line) 75%)'
  }
}

// ─── Skeleton section (3 skeleton cards with header) ─────────────────────────

export const TriageSectionSkeleton = {
  flow: 'y',
  gap: 'A',

  // Ensure keyframes exist before any child references them.
  onInit: (el) => {
    if (typeof document === 'undefined') return
    if (document.querySelector('style[data-triage-skeleton-kf]')) return
    const st = document.createElement('style')
    st.setAttribute('data-triage-skeleton-kf', '')
    st.textContent = [
      '@keyframes triageFadeIn { from { opacity: 0 } to { opacity: 1 } }',
      '@keyframes skeletonShimmer {',
      '  0% { background-position: -200% 0 }',
      '  100% { background-position: 200% 0 }',
      '}'
    ].join('\n')
    document.head.appendChild(st)
  },

  Header: {
    flow: 'x',
    gap: 'V',
    align: 'baseline left',
    paddingBottom: 'V',
    borderBottomWidth: 'V',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',

    Dot: {
      tag: 'span',
      width: 'Z', height: 'Z',
      round: 'round', flexShrink: '0',
      background: 'line'
    },
    TitleLine: {
      width: '5em', height: 'Z1', round: 'Y2',
      background: 'line',
      animation: 'skeletonShimmer 1.5s linear infinite',
      backgroundSize: '200% 100%',
      backgroundImage: 'linear-gradient(90deg, var(--color-line) 25%, var(--color-overlay) 50%, var(--color-line) 75%)'
    }
  },

  Cards: {
    flow: 'y', gap: 'V',
    SkeletonCard_1: { extends: 'TriageSkeleton' },
    SkeletonCard_2: { extends: 'TriageSkeleton' },
    SkeletonCard_3: { extends: 'TriageSkeleton' }
  }
}

// ─── Retry CTA (shown when initial fetch times out) ──────────────────────────

export const TriageRetryCTA = {
  flow: 'y',
  gap: 'A',
  align: 'center center',
  padding: 'C B1',
  textAlign: 'center',

  Icon: { tag: 'span', fontSize: 'C', text: '!' },
  Message: {
    tag: 'p',
    margin: '0',
    fontSize: 'A',
    fontWeight: '600',
    color: 'title',
    text: 'Couldn\'t load tickets'
  },
  Sub: {
    tag: 'p',
    margin: '0',
    fontSize: 'Y1',
    color: 'caption',
    text: 'The request timed out. Check your connection and try again.'
  },
  RetryBtn: {
    tag: 'button',
    padding: 'Z A',
    round: 'Y2',
    border: 'none',
    cursor: 'pointer',
    fontSize: 'A',
    fontWeight: '700',
    background: 'cyan',
    color: 'white',
    text: 'Retry',
    onClick: (e, el, s) => {
      const root = s.root || el.getRoot()
      if (!root) return
      root.update({ triageLoading: true, triageError: null })
      ticketStore.refresh().catch(() => {
        root.update({ triageLoading: false, triageError: 'timeout' })
      })
    }
  }
}

// ─── Virtualized card list ─────────────────────────────────────────────────────
//
// For buckets with many tickets, renders only VIRTUAL_BATCH visible cards
// initially. An IntersectionObserver on a sentinel at the bottom of the list
// triggers mounting the next batch when the user scrolls near the end.
//
// DOMQL-native: no third-party lib.
//
// v3.14 Reactive pattern (QA fix #4498):
//   Each TriageSection instance owns `state: { visN: VIRTUAL_INITIAL }`.
//   Groups.children reads `el.parent.state.visN` — a proper reactive signal —
//   so the createEffect in DOMQL v3.14 creates a live subscription. When
//   _expandSection updates `sectionEl.state.update({ visN: newVal })`, the
//   effect re-fires and reconciles the children array in place (keyed
//   childrenAs:'state' path in create.js).
//
//   The previous approach stored visN on root state under a dynamic key
//   (_visN_<sectionKey>). Because these keys did not exist at effect-creation
//   time, DOMQL's signal tracker had no subscription to trigger, so the
//   children factory never re-ran on expand — all rows rendered at once.

const VIRTUAL_BATCH = 20   // cards to add per scroll event
const VIRTUAL_INITIAL = 20 // cards to render on first paint

// Walk up from any descendant element to find the nearest TriageSection ancestor.
// Used by VirtualSentinel and MoreBtn which sit 2 levels below TriageSection.
const _findTriageSection = (el) => {
  // el = Sentinel  → el.parent = Footer  → el.parent.parent = TriageSection
  let cur = el.parent
  while (cur) {
    if (cur._isTriageSection) return cur
    cur = cur.parent
  }
  return null
}

// Expand the visible window for a TriageSection by VIRTUAL_BATCH.
// Operates on the section element's own reactive state (not root state).
const _expandSection = (sectionEl) => {
  if (!sectionEl || !sectionEl.state) return
  const cur = sectionEl.state.visN || VIRTUAL_INITIAL
  sectionEl.state.update({ visN: cur + VIRTUAL_BATCH })
}

// Sentinel element — IntersectionObserver target at the end of each bucket list.
// When it enters the viewport, triggers _expandSection on the parent TriageSection.
export const VirtualSentinel = {
  height: '1px',
  width: '100%',
  flexShrink: '0',

  onInit: (el) => {
    if (typeof IntersectionObserver === 'undefined') return
    const sectionEl = _findTriageSection(el)
    if (!sectionEl) return
    el._observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) _expandSection(sectionEl)
      }
    }, { rootMargin: '200px' })
    if (el.node) el._observer.observe(el.node)
  },

  onRemove: (el) => {
    if (el._observer) { el._observer.disconnect(); el._observer = null }
  }
}

// ─── Page main ────────────────────────────────────────────────────────────────

export const TicketsGeneralMain = {
  extends: 'AppMain',

  // Inject @keyframes for agent-dot pulse + count-chip scale animations once on mount.
  onInit: (el, s) => {
    if (typeof document === 'undefined') return
    if (!document.querySelector('style[data-agent-dot-keyframes]')) {
      const st = document.createElement('style')
      st.setAttribute('data-agent-dot-keyframes', '')
      st.textContent = [
        '@keyframes agentDotPulse {',
        '  0%, 100% { transform: scale(1); opacity: 1; }',
        '  50% { transform: scale(1.4); opacity: 0.6; }',
        '}',
        '@keyframes agentDotFade {',
        '  0%, 100% { opacity: 1; }',
        '  50% { opacity: 0.4; }',
        '}'
      ].join('\n')
      document.head.appendChild(st)
    }
    // AC12: Inject density CSS rules so [data-density=compact] attribute
    // on the Layout container reduces card padding/gap regardless of whether
    // the reactive padding prop re-evaluates on childrenAs:state cards.
    if (!document.querySelector('style[data-density-rules]')) {
      const st3 = document.createElement('style')
      st3.setAttribute('data-density-rules', '')
      // AC12: target .triage-card class — DOMQL assigns data-key using the
      // sibling index ("0","1",...) for unkeyed childExtends children, so
      // [data-key="TriageCard"] never matches at runtime.  The explicit
      // class added to TriageCard is stable regardless of render position.
      st3.textContent = [
        '[data-density="compact"] .triage-card {',
        '  padding-top: var(--spacing-Y) !important;',
        '  padding-bottom: var(--spacing-Y) !important;',
        '  gap: var(--spacing-Y) !important;',
        '}',
        '[data-density="compact"] .triage-card [data-key="ProgressBar"] {',
        '  margin-top: var(--spacing-V) !important;',
        '}',
        '[data-density="compact"] .triage-card [data-key="Labels"] {',
        '  display: none !important;',
        '}'
      ].join('\n')
      document.head.appendChild(st3)
    }
    // Keyframes for AC7/AC13: count chip scale bump on realtime delta.
    if (!document.querySelector('style[data-count-bump-kf]')) {
      const st2 = document.createElement('style')
      st2.setAttribute('data-count-bump-kf', '')
      st2.textContent = [
        '@keyframes countBump {',
        '  0% { transform: scale(1); }',
        '  40% { transform: scale(1.1); }',
        '  100% { transform: scale(1); }',
        '}',
        '@keyframes countBumpReduced {',
        '  0% { opacity: 1; }',
        '  50% { opacity: 0.7; }',
        '  100% { opacity: 1; }',
        '}'
      ].join('\n')
      document.head.appendChild(st2)
    }
    // Initialise density + selection state
    const root = s.root || el.getRoot()
    if (root && !root.densityMode) root.update({ densityMode: 'comfortable' })
    if (root && !root.selectedTicketIds) root.update({ selectedTicketIds: [] })
    if (root && root.kbFocusBucket === undefined) {
      root.update({ kbFocusBucket: null, kbFocusIndex: 0 })
    }

    // Global keyboard shortcuts — 'c' for quick-create, Esc to clear selection/focus
    if (typeof window !== 'undefined' && !window.__smblsTriageKbGlobal) {
      window.__smblsTriageKbGlobal = true
      const globalHandler = (e) => {
        const tag = (e.target?.tagName || '').toLowerCase()
        const isInput = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.contentEditable === 'true'
        if (isInput) return

        if (e.key === 'c' && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
          // AC11: Focus quick-create input in active bucket
          const qc = document.querySelector('[data-quick-create-input]')
          if (qc) { e.preventDefault(); qc.focus() }
          return
        }
        if (e.key === 'Escape') {
          const rootEl = el.getRoot()
          if (rootEl) rootEl.update({ selectedTicketIds: [], selectedTicket: null, kbFocusBucket: null, kbFocusIndex: 0 })
          // AC5: clear ?ticket= URL param unconditionally — safe no-op when absent.
          // Do NOT gate on rootEl.selectedTicket: when the modal was opened via
          // ?ticket= URL param, selectedTicket may be falsy in this closure even
          // though the modal is visible (sibling subtree updated root.selectedTicket
          // after this handler was registered).
          if (typeof window !== 'undefined') {
            const _u = new URL(window.location)
            if (_u.searchParams.has('ticket')) {
              _u.searchParams.delete('ticket')
              window.history.replaceState(null, '', _u)
            }
          }
          return
        }

        // Arrow-key navigation — only when not in an input
        const rootEl = el.getRoot()
        if (!rootEl) return
        const BUCKETS_ORDER = ['pendingApproval', 'now', 'ongoing', 'blocked', 'upcoming']
        const triage = rootEl.triage
        if (!triage) return

        const _getBucketRows = (bk) => {
          const raw = (triage[bk]) || []
          return raw
        }

        let bk = rootEl.kbFocusBucket || 'now'
        let idx = rootEl.kbFocusIndex || 0

        if (e.key === 'ArrowDown') {
          e.preventDefault()
          const rows = _getBucketRows(bk)
          idx = Math.min(idx + 1, rows.length - 1)
          rootEl.update({ kbFocusBucket: bk, kbFocusIndex: idx })
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          idx = Math.max(idx - 1, 0)
          rootEl.update({ kbFocusBucket: bk, kbFocusIndex: idx })
        } else if (e.key === 'ArrowRight') {
          e.preventDefault()
          const cur = BUCKETS_ORDER.indexOf(bk)
          const next = BUCKETS_ORDER[Math.min(cur + 1, BUCKETS_ORDER.length - 1)]
          rootEl.update({ kbFocusBucket: next, kbFocusIndex: 0 })
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault()
          const cur = BUCKETS_ORDER.indexOf(bk)
          const prev = BUCKETS_ORDER[Math.max(cur - 1, 0)]
          rootEl.update({ kbFocusBucket: prev, kbFocusIndex: 0 })
        } else if (e.key === 'Enter') {
          e.preventDefault()
          const rows = _getBucketRows(bk)
          const ticket = rows[idx]
          if (ticket) rootEl.update({ selectedTicket: { ...ticket } })
        }
      }
      window.addEventListener('keydown', globalHandler)
      el._globalKbHandler = globalHandler
    }
  },

  onRemove: (el) => {
    if (typeof window !== 'undefined' && el._globalKbHandler) {
      window.removeEventListener('keydown', el._globalKbHandler)
      el._globalKbHandler = null
      window.__smblsTriageKbGlobal = false
    }
  },

  Header: { extends: 'TicketsHeader' },

  Body: {
    overflow: 'auto',

    Hero: {
      extends: 'PageHero',
      eyebrow: 'WORKSPACE · LIVE TRIAGE',
      headline: 'General',
      lede: (el, s) => {
        const t = s.root.triage
        if (s.root.triageLoading && !t) return 'Loading triage…'
        if (s.root.triageError) return 'Failed to load triage — see retry below'
        if (!t) return 'Loading triage…'
        const n = (key) => _sectionRows(s, key).length
        const ready = _readyToTestRows(s).length
        const pending = _pendingApprovalRows(s).length
        const blocked = _blockedRows(s).length
        const decisions = _decisionRows(s).length
        const scope = s.root.filterEpic ? `epic:${s.root.filterEpic}` : (s.root.filterAssignee || 'all')
        const pendingStr = pending > 0 ? ` · ${pending} pending approval` : ''
        const blockedStr = blocked > 0 ? ` · ${blocked} blocked` : ''
        const decisionsStr = decisions > 0 ? ` · ${decisions} decisions` : ''
        return `${n('now')} now · ${n('ongoing')} ongoing${blockedStr} · ${n('upcoming')} upcoming · ${ready} ready-to-test${pendingStr}${decisionsStr} (scope: ${scope})`
      },

      EpicChip: {
        hide: (el, s) => !s.root.filterEpic,
        flow: 'x',
        gap: 'Z',
        align: 'center left',
        marginTop: 'Z',
        fontSize: 'Y1',
        Label: {
          tag: 'span',
          padding: 'V Z',
          round: 'V',
          background: 'cyan.15',
          color: 'cyan',
          fontWeight: '700',
          text: (el, s) => `epic: ${s.root.filterEpic}`
        },
        Clear: {
          tag: 'button',
          padding: 'V Z',
          round: 'V',
          border: 'none',
          cursor: 'pointer',
          background: 'line',
          color: 'title',
          fontSize: 'Y2',
          fontWeight: '600',
          text: 'clear',
          onClick: (e, el, s) => {
            const root = s.root || el.getRoot()
            if (!root) return
            root.update({ filterEpic: null })
            if (typeof window !== 'undefined') {
              const u = new URL(window.location)
              u.searchParams.delete('epic')
              window.history.replaceState(null, '', u)
            }
          }
        }
      }
    },

    // AC12: Density toggle + Layer 3: Group-by control bar
    ControlRow: {
      flow: 'x',
      align: 'center space-between',
      padding: 'Z B1',
      gap: 'A',
      borderBottomWidth: 'V',
      borderBottomStyle: 'solid',
      borderBottomColor: 'line',
      maxWidth: 'L+L',
      width: '100%',
      margin: '0 auto',

      GroupByBar: { extends: 'GroupByControl', padding: '0', border: 'none', margin: '0', maxWidth: 'none' },
      DensityToggle: { extends: 'TriageDensityToggle' }
    },

    Layout: {
      display: 'grid',
      gap: 'B',
      padding: 'C B1 D B1',
      maxWidth: 'L+L',
      width: '100%',
      margin: '0 auto',
      alignItems: 'flex-start',
      // AC12: propagate density as a DOM attribute so CSS rules injected in
      // onInit (data-density-rules) can target cards regardless of whether
      // their reactive padding prop re-evaluates on childrenAs:state children.
      'data-density': (el, s) => s.root.densityMode || 'comfortable',
      // Two-column layout at >=1280px: main content + 280px right rail.
      // Implemented via JS-driven state (viewportWidth) rather than @media CSS
      // because DOMQL's atomic CSS engine does not reliably emit @media compound
      // classes when a same-property flat class exists in the stylesheet
      // (source-order bug confirmed across QA failures #1 and #2, tickets 4386/4455).
      // Framework fix tracked in ticket 4472; this workaround is the app-side guard.
      gridTemplateColumns: (el, s) =>
        (s.root.viewportWidth || (typeof window !== 'undefined' ? window.innerWidth : 0)) >= 1280
          ? '1fr 280px'
          : '1fr',

      onInit: (el, s) => {
        if (typeof window === 'undefined') return
        const handler = () => {
          const root = s.root || el.getRoot()
          if (root && typeof root.update === 'function') {
            root.update({ viewportWidth: window.innerWidth })
          }
        }
        // Set initial value immediately
        handler()
        window.addEventListener('resize', handler, { passive: true })
        el._layoutResizeHandler = handler
      },

      onRemove: (el) => {
        if (typeof window !== 'undefined' && el._layoutResizeHandler) {
          window.removeEventListener('resize', el._layoutResizeHandler)
          el._layoutResizeHandler = null
        }
      },

      Sections: {
        flow: 'y',
        gap: 'C',
        minWidth: '0',

        // Loading skeleton — shown while first fetch is in-flight.
        // Fades out when triage data arrives. Per spec E: no page-wide spinner.
        LoadingSkeleton: {
          hide: (el, s) => !!(s.root.triage || s.root.triageError),
          flow: 'y',
          gap: 'C',
          SkeletonNow: { extends: 'TriageSectionSkeleton' },
          SkeletonOngoing: { extends: 'TriageSectionSkeleton' }
        },

        // Retry CTA — shown when fetch times out (spec F).
        RetryCTA: {
          hide: (el, s) => !s.root.triageError,
          extends: 'TriageRetryCTA'
        },

        // Main content — hidden until triage is available.
        TriageContent: {
          hide: (el, s) => !s.root.triage,
          flow: 'y',
          gap: 'C',

          // AC11: Quick-create input at top of sections
          QuickCreate: { extends: 'TriageQuickCreate' },

          PendingApprovals: { extends: 'PendingApprovalsSection' },
          Decisions: { extends: 'DecisionsSection' },

          // State-grouped view (default)
          StateSections: {
            flow: 'y',
            gap: 'C',
            hide: (el, s) => !!(s.root.generalGroupBy && s.root.generalGroupBy !== 'state'),

            Now: {
              extends: 'TriageSection',
              sectionKey: 'now',
              accent: 'green',
              title: 'Now',
              subtitle: 'Agents actively working right now · live heartbeat < 10m'
            },
            Ongoing: {
              extends: 'TriageSection',
              sectionKey: 'ongoing',
              accent: 'yellow',
              title: 'Ongoing',
              subtitle: 'Picked up but no live heartbeat · agent paused or between ticks'
            },
            Blocked: { extends: 'BlockedSection' },
            Upcoming: {
              extends: 'TriageSection',
              sectionKey: 'upcoming',
              accent: 'cyan',
              title: 'Upcoming',
              subtitle: 'Assigned but not yet picked up · ranked by priority'
            }
          },

          // Flat grouped view (non-state groupings)
          FlatView: {
            flow: 'y',
            gap: 'C',
            hide: (el, s) => !s.root.generalGroupBy || s.root.generalGroupBy === 'state',

            Groups: {
              flow: 'y',
              gap: 'C',

              childExtends: 'TriageFlatGroup',
              childrenAs: 'state',
              children: (el, s) => _buildFlatGroups(_allRows(s), s.root.generalGroupBy || 'none')
            }
          }
        }
      },

      RightRails: {
        flow: 'y',
        gap: 'B',
        minWidth: '0',  // Prevent grid child from overflowing the 280px column
        // AC4: sticky-top alignment — alignSelf flex-start ensures sticky context is the grid cell
        alignSelf: 'flex-start',
        position: (el, s) =>
          (s.root.viewportWidth || (typeof window !== 'undefined' ? window.innerWidth : 0)) >= 1280
            ? 'sticky' : 'static',
        top: (el, s) =>
          (s.root.viewportWidth || (typeof window !== 'undefined' ? window.innerWidth : 0)) >= 1280
            ? 'var(--spacing-A)' : 'auto',
        // Right rails hidden below 1280px via parent grid collapse
        ReadyRail: { extends: 'ReadyToTestRail' },
        AgentRail: { extends: 'AgentPulseRail' }
      }
    }
  },

  // AC6: Multi-select action bar — fixed at viewport bottom when tickets selected
  SelectionBar: { extends: 'TriageSelectionActionBar' }
}

// ─── Group-by control ─────────────────────────────────────────────────────────

// AC3: GroupByControl — real segmented control with kbd hint badges.
// The segment group uses a single outer border (border on the container) with
// no gap between options so it reads as one unified control, not separate chips.
export const GroupByControl = {
  flow: 'x',
  gap: 'Z',
  align: 'center left',
  padding: 'Z B1',
  borderBottomWidth: 'V',
  borderBottomStyle: 'solid',
  borderBottomColor: 'line',
  flexWrap: 'wrap',
  maxWidth: 'L+L',
  width: '100%',
  margin: '0 auto',

  onInit: (el, s) => {
    if (typeof window === 'undefined') return
    const handler = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return
      const idx = parseInt(e.key, 10) - 1
      if (idx >= 0 && idx < GROUP_BY_OPTIONS.length) {
        const root = s.root || el.getRoot()
        if (root) _setGroupBy(root, GROUP_BY_OPTIONS[idx].groupKey)
      }
    }
    window.addEventListener('keydown', handler)
    el._groupByKbHandler = handler
  },

  onRemove: (el) => {
    if (typeof window !== 'undefined' && el._groupByKbHandler) {
      window.removeEventListener('keydown', el._groupByKbHandler)
      el._groupByKbHandler = null
    }
  },

  Label: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    fontWeight: '500',
    letterSpacing: '.04em',
    text: 'Group by'
  },

  // Segmented control container — single rounded pill wrapping all options.
  // Borders between segments are created via right-border on each non-last item.
  SegmentedGroup: {
    flow: 'x',
    round: 'Z',
    overflow: 'hidden',
    borderWidth: 'V',
    borderStyle: 'solid',
    borderColor: 'line',

    childExtends: 'GroupByChip',
    childrenAs: 'state',
    children: (el, s) => GROUP_BY_OPTIONS.map((opt, i) => ({
      ...opt,
      _index: i + 1,
      _isLast: i === GROUP_BY_OPTIONS.length - 1,
      isActive: (s.root.generalGroupBy || 'state') === opt.groupKey
    }))
  }
}

// AC3: GroupByChip — segment inside the segmented control.
// No outer border (outer border belongs to SegmentedGroup).
// Divider line via right-border on non-last segments.
export const GroupByChip = {
  tag: 'button',
  flow: 'x',
  gap: 'Y',
  align: 'center center',
  padding: 'Y Z',
  fontSize: 'Y2',
  fontWeight: (el, s) => s.isActive ? '700' : '500',
  cursor: 'pointer',
  border: 'none',
  borderRightWidth: (el, s) => s._isLast ? '0' : 'V',
  borderRightStyle: 'solid',
  borderRightColor: 'line',
  background: (el, s) => s.isActive ? 'cyan.15' : 'transparent',
  color: (el, s) => s.isActive ? 'cyan' : 'caption',
  transition: 'background 0.12s ease, color 0.12s ease',
  ':hover': {
    background: (el, s) => s.isActive ? 'cyan.15' : 'overlay',
    color: (el, s) => s.isActive ? 'cyan' : 'title'
  },
  title: (el, s) => `Group by ${s.label} (press ${s._index})`,

  Text: { tag: 'span', text: (el, s) => s.label },

  // Keyboard shortcut rendered as a styled kbd badge — separate from label text
  Shortcut: {
    tag: 'kbd',
    fontSize: 'V',
    fontWeight: '600',
    padding: 'V Y',
    round: 'Y',
    lineHeight: '1',
    color: (el, s) => s.isActive ? 'cyan' : 'caption',
    background: (el, s) => s.isActive ? 'cyan.08' : 'line',
    borderWidth: 'V',
    borderStyle: 'solid',
    borderColor: (el, s) => s.isActive ? 'cyan.3' : 'overlayStrong',
    fontFamily: 'monospace',
    text: (el, s) => `${s._index}`
  },

  onClick: (e, el, s) => {
    const root = s.root || el.getRoot()
    if (root) _setGroupBy(root, s.groupKey)
  }
}

// ─── Triage section (state-grouped default) ───────────────────────────────────

export const TriageSection = {
  flow: 'y',
  gap: 'A',

  // Each instance owns its own reactive state for virtualization.
  // visN is the visible-card count; initialized to VIRTUAL_INITIAL.
  // Groups.children reads el.parent.state.visN — this creates a proper
  // reactive subscription in DOMQL v3.14's createEffect so expand triggers re-render.
  state: { visN: VIRTUAL_INITIAL },

  // Marker flag so _findTriageSection can locate this element imperatively.
  _isTriageSection: true,

  onInit: (el) => {
    // Ensure visN is set (defensive: state may already be initialized from
    // extends chain; only set if missing so we don't clobber an expansion).
    if (el.state && el.state.visN === undefined) {
      el.state.update({ visN: VIRTUAL_INITIAL })
    }
  },

  hide: (el, s) => _sectionRows(s, el.sectionKey).length === 0,

  // Semantic left-border accent stripe per bucket (token-based, no raw px).
  // The accent color is passed as a prop from the parent bucket definition:
  //   Now → green, Ongoing → yellow, Upcoming → cyan (see TicketsGeneralMain).
  borderLeftWidth: 'V',
  borderLeftStyle: 'solid',
  borderLeftColor: (el, s) => `var(--color-${el.accent || 'line'})`,
  paddingLeft: 'A',

  Header: {
    flow: 'x',
    gap: 'Z',
    align: 'center left',
    paddingBottom: 'Z',
    marginBottom: 'V',
    borderBottomWidth: 'V',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',

    // Accent dot — sized with spacing token Z (≈8px in golden ratio scale at base:17)
    Dot: {
      tag: 'span',
      width: 'Z',
      height: 'Z',
      round: 'round',
      flexShrink: '0',
      alignSelf: 'center',
      background: (el, s) => `var(--color-${el.parent.parent.accent || 'cyan'})`,
      boxShadow: (el, s) => `0 0 10px color-mix(in srgb, var(--color-${el.parent.parent.accent || 'cyan'}) 55%, transparent)`
    },
    Title: {
      tag: 'h2',
      fontSize: 'A',
      fontWeight: '700',
      letterSpacing: '-.02em',
      color: 'title',
      margin: '0',
      lineHeight: '1.2',
      text: (el, s) => el.parent.parent.title || ''
    },
    // AC7/AC13: Count chip with scale-bump animation on realtime delta.
    // Tracks previous count on el._prevCount; on change, fires a CSS animation
    // via node.style (one-time transient, cleared after animation).
    // prefers-reduced-motion guard: use opacity fade instead of scale.
    Count: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      padding: 'V Y',
      round: 'C',
      display: 'inline-block',
      transformOrigin: 'center center',
      color: (el, s) => `var(--color-${el.parent.parent.accent || 'cyan'})`,
      background: (el, s) => `color-mix(in srgb, var(--color-${el.parent.parent.accent || 'cyan'}) 12%, transparent)`,
      text: (el, s) => {
        const n = _sectionRows(s, el.parent.parent.sectionKey).length
        // Trigger bump animation when count changes
        if (typeof el._prevCount === 'number' && el._prevCount !== n && el.node) {
          const prefersReduced = typeof window !== 'undefined' &&
            window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
          const animName = prefersReduced ? 'countBumpReduced' : 'countBump'
          el.node.style.animation = 'none'
          // Reflow to restart animation
          void el.node.offsetWidth
          el.node.style.animation = `${animName} 0.3s ease`
          el.node.addEventListener('animationend', () => {
            if (el.node) el.node.style.animation = ''
          }, { once: true })
        }
        el._prevCount = n
        return `${n}`
      }
    },
    Subtitle: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      fontStyle: 'italic',
      marginLeft: 'V',
      flex: '1',
      text: (el, s) => el.parent.parent.subtitle || ''
    }
  },

  // Virtualized assignee groups: render only VIRTUAL_BATCH cards per section.
  // Groups are always rendered (the header + count are always visible); only the
  // per-group card lists are sliced to the visible window.
  //
  // v3.14 reactive fix: reads el.parent.state.visN (the TriageSection's own signal)
  // instead of s.root._visN_<key>. DOMQL v3.14 createEffect tracks el.parent.state.visN
  // as a reactive dependency so children reconcile when _expandSection fires.
  Groups: {
    flow: 'y',
    gap: 'Z',

    childExtends: 'TriageGroup',
    childrenAs: 'state',
    children: (el, s) => {
      const sectionEl = el.parent
      const sectionKey = sectionEl.sectionKey
      // Read visN from section's OWN reactive state so createEffect subscribes.
      const vis = (sectionEl.state && sectionEl.state.visN) || VIRTUAL_INITIAL
      const allRows = _sectionRows(s, sectionKey)
      // Slice to visible window BEFORE grouping to keep grouping computation
      // cheap for large buckets.
      const sliced = allRows.slice(0, vis)
      return _groupByAssignee(sliced)
    }
  },

  // Load-more: IntersectionObserver sentinel + explicit button.
  Footer: {
    flow: 'y',
    gap: 'V',
    hide: (el, s) => {
      const sectionEl = el.parent
      const key = sectionEl.sectionKey
      const vis = (sectionEl.state && sectionEl.state.visN) || VIRTUAL_INITIAL
      return _sectionRows(s, key).length <= vis
    },

    // IntersectionObserver sentinel — triggers auto-expand on scroll near end.
    // Uses _findTriageSection to locate the ancestor (no sectionKey prop needed).
    Sentinel: {
      extends: 'VirtualSentinel'
    },

    // Explicit load-more button (redundant with sentinel but good for a11y).
    MoreBtn: {
      tag: 'button',
      padding: 'V A',
      round: 'Y2',
      border: 'none',
      cursor: 'pointer',
      fontSize: 'Y2',
      fontWeight: '600',
      background: 'overlay',
      color: 'caption',
      borderWidth: 'V',
      borderStyle: 'solid',
      borderColor: 'line',
      transition: 'border-color 0.12s ease, color 0.12s ease',
      ':hover': { borderColor: 'cyan', color: 'cyan' },
      text: (el, s) => {
        const sectionEl = el.parent.parent
        const key = sectionEl.sectionKey
        const total = _sectionRows(s, key).length
        const vis = (sectionEl.state && sectionEl.state.visN) || VIRTUAL_INITIAL
        const remaining = Math.max(0, total - vis)
        return `Load ${Math.min(remaining, VIRTUAL_BATCH)} more (${remaining} remaining)`
      },
      onClick: (e, el, s) => {
        const sectionEl = _findTriageSection(el)
        _expandSection(sectionEl)
      }
    }
  }
}

// ─── Assignee swimlane ────────────────────────────────────────────────────────

export const TriageGroup = {
  flow: 'y',
  gap: 'V',

  Head: {
    flow: 'x',
    gap: 'Z',
    align: 'center left',
    paddingLeft: 'V',

    Avatar: {
      tag: 'span',
      width: 'B',
      height: 'B',
      round: 'round',
      flexShrink: '0',
      align: 'center center',
      fontSize: 'Y2',
      fontWeight: '700',
      color: (el, s) => s.isLive ? 'green' : (s.isAgent ? 'cyan' : 'title'),
      background: (el, s) => s.isLive
        ? 'color-mix(in srgb, var(--color-green) 15%, transparent)'
        : (s.isAgent ? 'color-mix(in srgb, var(--color-cyan) 15%, transparent)' : 'line'),
      boxShadow: (el, s) => s.isLive ? '0 0 10px color-mix(in srgb, var(--color-green) 50%, transparent)' : 'none',
      text: (el, s) => (s.actor || '?').slice(0, 1).toUpperCase()
    },
    Actor: {
      tag: 'span',
      fontSize: 'Y1',
      fontWeight: '700',
      color: (el, s) => s.isLive ? 'green' : (s.isAgent ? 'cyan' : 'title'),
      letterSpacing: '.02em',
      text: (el, s) => s.actor || '?'
    },
    LiveTag: {
      hide: (el, s) => !s.isLive,
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      padding: 'V Z',
      round: 'V',
      background: 'var(--color-green-15, var(--color-green))',
      color: 'green',
      text: 'LIVE'
    },
    Count: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => `${(s.rows || []).length}`
    }
  },

  List: {
    flow: 'y',
    gap: 'V',
    paddingLeft: 'A',
    borderLeftWidth: 'V',
    borderLeftStyle: 'solid',
    borderLeftColor: 'line',

    childExtends: 'TriageCard',
    childrenAs: 'state',
    children: (el, s) => s.rows || []
  }
}

// ─── Flat group (non-state grouping) ─────────────────────────────────────────

export const TriageFlatGroup = {
  flow: 'y',
  gap: 'A',

  Header: {
    flow: 'x',
    gap: 'V',
    align: 'baseline left',
    paddingBottom: 'V',
    borderBottomWidth: 'V',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',
    cursor: 'pointer',

    CollapseBtn: {
      tag: 'button',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      padding: '0',
      fontSize: 'Y1',
      color: 'caption',
      transition: 'transform 0.12s ease',
      transform: (el, s) => {
        const root = s.root || el.getRoot()
        return (root && root[`_groupCollapsed_${s.groupKey}`]) ? 'rotate(-90deg)' : 'rotate(0deg)'
      },
      text: '▾',
      onClick: (e, el, s) => {
        e.stopPropagation()
        const root = s.root || el.getRoot()
        if (!root) return
        const key = `_groupCollapsed_${s.groupKey}`
        root.update({ [key]: !root[key] })
      }
    },
    Title: {
      tag: 'h2',
      fontSize: 'B',
      fontWeight: '700',
      letterSpacing: '-.02em',
      color: 'title',
      margin: '0',
      text: (el, s) => s.groupLabel || s.actor || s.groupKey || '?'
    },
    Count: {
      tag: 'span',
      fontSize: 'Y1',
      fontWeight: '600',
      color: 'caption',
      text: (el, s) => { const n = (s.rows || []).length; return `${n} ticket${n === 1 ? '' : 's'}` }
    }
  },

  List: {
    flow: 'y',
    gap: 'V',
    hide: (el, s) => {
      const root = s.root || el.getRoot()
      return !!(root && root[`_groupCollapsed_${s.groupKey}`])
    },

    childExtends: 'TriageCard',
    childrenAs: 'state',
    children: (el, s) => s.rows || []
  }
}

// ─── Layer 1+2: TriageCard ────────────────────────────────────────────────────
//
// Full ticket card with: external_id · state pill · priority pill ·
// title (h3) · parent breadcrumb · assignee avatar · labels ·
// last-activity · active-indicator pulsing dot · progress bar.

export const TriageCard = {
  // AC12: stable class for CSS density rules — data-key is the sibling index
  // ("0","1",...) for unkeyed childExtends children, so it cannot be targeted
  // reliably from injected CSS.  This class is always present on the DOM node.
  class: 'triage-card',
  flow: 'y',
  // AC1/AC12: density-responsive gap and padding (compact halves vertical space)
  gap: 'Y',
  padding: (el, s) => (s.root && s.root.densityMode === 'compact') ? 'Y A' : 'Z A',
  round: 'Z1',
  borderWidth: 'V',
  borderStyle: 'solid',
  // AC6: selected state border highlight
  borderColor: (el, s) => {
    const selectedIds = s.root && s.root.selectedTicketIds
    if (Array.isArray(selectedIds) && selectedIds.includes(s.id)) return 'cyan'
    if (s.liveAgent) return 'var(--color-green-a+30, var(--color-green))'
    if (s.isBlocked) return 'var(--color-red-a+20, var(--color-red))'
    return 'line'
  },
  // AC6: selected state background highlight + base overlay
  background: (el, s) => {
    const selectedIds = s.root && s.root.selectedTicketIds
    if (Array.isArray(selectedIds) && selectedIds.includes(s.id)) {
      return 'color-mix(in srgb, var(--color-cyan) 8%, var(--color-overlay))'
    }
    return 'overlay'
  },
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  transition: 'border-color 0.12s ease, transform 0.14s ease, box-shadow 0.14s ease, background 0.12s ease',
  // AC5: keyboard focus ring
  outline: (el, s) => {
    const bk = s.root && s.root.kbFocusBucket
    const idx = s.root && s.root.kbFocusIndex
    if (!bk || idx === undefined) return 'none'
    // Match by bucket + index — el._kbBucket and el._kbIndex are set when cards render
    if (el._kbBucket === bk && el._kbIndex === idx) {
      return '2px solid var(--color-cyan)'
    }
    return 'none'
  },
  outlineOffset: '2px',
  ':hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 3px 12px var(--color-shadow)',
    borderColor: (el, s) => s.liveAgent ? 'green' : (s.isBlocked ? 'red' : 'overlayStrong')
  },
  ':active': { transform: 'translateY(0)' },
  draggable: 'true',
  'data-triage-card-id': (el, s) => s.id,
  // AC6: test-observable selected state — QA reads via
  //   card.dataset.testSelected === 'true'
  'data-test-selected': (el, s) => {
    const selectedIds = s.root && s.root.selectedTicketIds
    return (Array.isArray(selectedIds) && selectedIds.includes(s.id)) ? 'true' : 'false'
  },

  onClick: (e, el, s) => {
    const root = s.root || el.getRoot()
    if (!root || typeof root.update !== 'function') return

    // AC6: Cmd/Ctrl+click — toggle individual selection
    // selectedTicketIds is stored as an Array (DOMQL v3.14 state serialises Sets
    // to plain objects which are not iterable — always use Array in root state).
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault()
      const rawCur = root.selectedTicketIds
      const cur = Array.isArray(rawCur) ? rawCur : []
      const next = cur.includes(s.id) ? cur.filter(id => id !== s.id) : [...cur, s.id]
      root.update({ selectedTicketIds: next })
      return
    }

    // AC6: Shift+click — range select from last selected to this
    if (e.shiftKey) {
      e.preventDefault()
      const rawCur = root.selectedTicketIds
      const cur = Array.isArray(rawCur) ? rawCur : []
      if (cur.length === 0) {
        root.update({ selectedTicketIds: [s.id] })
        return
      }
      // Build ordered list of all visible cards by iterating DOM order
      const allCards = document.querySelectorAll('[data-triage-card-id]')
      const ids = Array.from(allCards).map(c => Number(c.dataset.triageCardId))
      const lastId = cur[cur.length - 1]
      const a = ids.indexOf(lastId)
      const b = ids.indexOf(s.id)
      if (a === -1 || b === -1) {
        const next = cur.includes(s.id) ? cur : [...cur, s.id]
        root.update({ selectedTicketIds: next })
        return
      }
      const [lo, hi] = a < b ? [a, b] : [b, a]
      const rangeIds = ids.slice(lo, hi + 1).filter(id => id != null)
      const merged = [...new Set([...cur, ...rangeIds])]
      root.update({ selectedTicketIds: merged })
      return
    }

    // Normal click — open detail modal (clear selection)
    root.update({ selectedTicket: { ...s } })
  },

  onDragstart: (e, el, s) => {
    try {
      e.dataTransfer.setData('text/ticket-id', String(s.id))
      e.dataTransfer.effectAllowed = 'move'
    } catch (_) {}
  },

  // Top row: id · active dot · state pill · priority pill
  TopRow: {
    flow: 'x',
    gap: 'Z',
    align: 'center left',
    flexWrap: 'wrap',

    Ref: {
      tag: 'span',
      flexShrink: '0',
      fontSize: 'Y2',
      fontWeight: '600',
      color: 'caption',
      fontFamily: 'monospace',
      text: (el, s) => s.external_id || `#${s.id}`
    },

    // Pulsing dot — shown when a live agent is on this ticket
    ActiveDot: {
      display: (el, s) => s.liveAgent ? 'inline-block' : 'none',
      tag: 'span',
      width: 'Z',
      height: 'Z',
      round: 'round',
      flexShrink: '0',
      background: 'green',
      title: (el, s) => s.liveAgent ? `${s.liveAgent} is working on this` : '',
      // Deliver animation via DOMQL CSS class props, not style:{} inline.
      // animationName is split across two @media queries (both compound classes)
      // rather than a flat prop + @media override — this avoids the CSS source-order
      // bug where a flat prop class is inserted after the compound @media class,
      // causing the flat rule to always win regardless of viewport/OS preference.
      animationDuration: '1.4s',
      animationTimingFunction: 'ease-in-out',
      animationIterationCount: 'infinite',
      animationFillMode: 'none',
      '@media (prefers-reduced-motion: no-preference)': { animationName: 'agentDotPulse' },
      '@media (prefers-reduced-motion: reduce)': { animationName: 'agentDotFade' }
    },

    StatePill: {
      display: (el, s) => s.state ? 'inline-flex' : 'none',
      tag: 'span',
      align: 'center center',
      padding: 'V Z',
      round: 'C',
      fontSize: 'V',
      fontWeight: '700',
      letterSpacing: '.04em',
      color: (el, s) => {
        if (s.liveAgent) return 'green'
        return TICKET_STATE_COLOR[s.state] || 'caption'
      },
      background: (el, s) => {
        const c = s.liveAgent ? 'green' : (TICKET_STATE_COLOR[s.state] || 'caption')
        return `color-mix(in srgb, var(--color-${c}) 14%, transparent)`
      },
      text: (el, s) => {
        if (s.liveAgent) {
          const age = s.liveAgentHeartbeat
            ? Math.max(0, Math.floor((Date.now() - new Date(s.liveAgentHeartbeat).getTime()) / 60000))
            : 0
          return `live · ${age}m`
        }
        return formatStateLabel(s.state || 'not_started')
      }
    },

    PriorityPill: {
      display: (el, s) => s.priority ? 'inline-flex' : 'none',
      tag: 'span',
      padding: 'V Z',
      round: 'C',
      fontSize: 'V',
      fontWeight: '700',
      letterSpacing: '.04em',
      color: (el, s) => {
        if (s.priority === 'P0') return 'red'
        if (s.priority === 'P1') return 'orange'
        if (s.priority === 'P2') return 'yellow'
        return 'caption'
      },
      background: (el, s) => {
        const c = s.priority === 'P0' ? 'red' : s.priority === 'P1' ? 'orange'
          : s.priority === 'P2' ? 'yellow' : 'caption'
        return `color-mix(in srgb, var(--color-${c}) 14%, transparent)`
      },
      text: (el, s) => s.priority || ''
    }
  },

  // Title row — H3, 2-line clamp. Tighter font size (Z2) vs old A for density.
  Title: {
    tag: 'h3',
    fontSize: 'Z2',
    fontWeight: '600',
    letterSpacing: '-.01em',
    lineHeight: '1.3',
    color: 'title',
    margin: '0',
    overflow: 'hidden',
    // 2-line clamp via webkit — applied inline after mount to avoid
    // conflicting with DOMQL's display management.
    onInit: (el) => {
      if (!el.node) return
      el.node.style.display = '-webkit-box'
      el.node.style['-webkit-line-clamp'] = '2'
      el.node.style['-webkit-box-orient'] = 'vertical'
      el.node.style.overflow = 'hidden'
    },
    text: (el, s) => (s.title || '').replace(/^B-\d+\s*·\s*/, '')
  },

  // Meta row: parent path (epic › story) + assignee avatar
  MetaRow: {
    flow: 'x',
    gap: 'Z',
    align: 'center left',
    flexWrap: 'wrap',

    // Renamed from 'Breadcrumb' to avoid auto-extending the brand Breadcrumb
    // component (which calls el.getData("pages") and throws in the intranet
    // context, crashing every TriageCard creation inside createEffect).
    ParentPath: {
      display: (el, s) => (s.parentEpicTitle || s.parentStoryTitle) ? 'flex' : 'none',
      flow: 'x',
      gap: 'V',
      align: 'center left',
      fontSize: 'Y2',
      color: 'caption',
      flex: '1',
      minWidth: '0',
      overflow: 'hidden',

      Epic: {
        display: (el, s) => s.parentEpicTitle ? 'inline' : 'none',
        tag: 'span',
        color: 'cyan',
        fontWeight: '600',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => s.parentEpicTitle || ''
      },
      Sep: {
        display: (el, s) => (s.parentEpicTitle && s.parentStoryTitle) ? 'inline' : 'none',
        tag: 'span',
        opacity: '.4',
        text: '›'
      },
      Story: {
        display: (el, s) => s.parentStoryTitle ? 'inline' : 'none',
        tag: 'span',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => s.parentStoryTitle || ''
      }
    },

    Assignee: {
      display: (el, s) => s.assignee_email ? 'flex' : 'none',
      flow: 'x',
      gap: 'V',
      align: 'center left',
      flexShrink: '0',
      marginLeft: 'auto',

      Avatar: {
        tag: 'span',
        width: 'B',
        height: 'B',
        round: 'round',
        flexShrink: '0',
        align: 'center center',
        fontSize: 'Y2',
        fontWeight: '700',
        color: 'title',
        background: (el, s) => _isAgentEmail(s.assignee_email) ? 'cyan.15' : 'line',
        title: (el, s) => s.assignee_email || '',
        text: (el, s) => {
          const e = s.assignee_email || ''
          if (_isAgentEmail(e)) return e.split('-agent')[0].slice(0, 1).toUpperCase()
          return e.split('@')[0].slice(0, 1).toUpperCase()
        }
      },
      Name: {
        tag: 'span',
        fontSize: 'Y2',
        color: (el, s) => _isAgentEmail(s.assignee_email) ? 'cyan' : 'caption',
        text: (el, s) => {
          const e = s.assignee_email || ''
          if (_isAgentEmail(e)) return e.split('-agent')[0].toUpperCase()
          return e.split('@')[0]
        }
      }
    }
  },

  // Labels row — up to 3 visible + "+N more" indicator
  Labels: {
    display: (el, s) => (s.labels || []).length ? 'flex' : 'none',
    flow: 'x',
    gap: 'Y',
    flexWrap: 'wrap',

    childExtends: 'TriageLabelChip',
    childrenAs: 'state',
    children: (el, s) => {
      const labels = s.labels || []
      const MAX = 3
      const visible = labels.slice(0, MAX).map(label => ({ label, _more: 0 }))
      const remaining = labels.length - MAX
      if (remaining > 0) visible.push({ label: `+${remaining}`, _more: remaining })
      return visible
    }
  },

  // Last-activity line
  LastActivity: {
    display: (el, s) => (s.lastActivityAt || s.updatedAt) ? 'flex' : 'none',
    flow: 'x',
    gap: 'V',
    align: 'center left',
    fontSize: 'Y2',
    color: 'caption',

    Icon: {
      tag: 'span',
      text: (el, s) => s.liveAgent ? '●' : '○'
    },
    Text: {
      tag: 'span',
      title: (el, s) => s.lastActivityAt || s.updatedAt || '',
      text: (el, s) => {
        if (s.liveAgent) return `${s.liveAgent} is working on this`
        if (s.lastActivityAgent && s.lastActivityAt) {
          return `${s.lastActivityAgent} · ${_relativeTime(s.lastActivityAt)}`
        }
        if (s.updatedAt) return `updated ${_relativeTime(s.updatedAt)}`
        return ''
      }
    }
  },

  // Blocker info — shown only on blocked tickets (s.isBlocked && s.blockedByIds.length > 0).
  // Lists each blocker as "blocked by #N" with a link to open that ticket's detail.
  BlockerInfo: {
    display: (el, s) => (s.isBlocked && s.blockedByIds && s.blockedByIds.length > 0) ? 'flex' : 'none',
    flow: 'x',
    gap: 'V',
    align: 'center left',
    flexWrap: 'wrap',
    fontSize: 'Y2',
    color: 'red',

    Icon: {
      tag: 'span',
      flexShrink: '0',
      text: '⊘',
      title: 'Blocked by'
    },
    Label: {
      tag: 'span',
      flexShrink: '0',
      fontWeight: '600',
      color: 'red',
      opacity: '.7',
      text: 'blocked by'
    },
    Blockers: {
      flow: 'x',
      gap: 'Y',
      flexWrap: 'wrap',
      align: 'center left',

      childExtends: 'BlockerChip',
      childrenAs: 'state',
      children: (el, s) => s.blockedByIds || []
    }
  },

  // Layer 2: Progress bar — thin track at card bottom, no label text (spec)
  ProgressBar: {
    position: 'relative',
    width: '100%',
    height: 'Y',
    round: 'V',
    background: (el, s) => s.isBlocked ? 'color-mix(in srgb, var(--color-red) 15%, transparent)' : 'line',
    overflow: 'hidden',
    marginTop: 'Y',

    Fill: {
      position: 'absolute',
      top: '0',
      left: '0',
      height: '100%',
      round: 'V',
      transition: 'width 0.4s ease',
      width: (el, s) => `${s.progressPct || 0}%`,
      background: (el, s) => {
        if (s.isBlocked) return 'var(--color-red)'
        if (s.liveAgent) return 'var(--color-green)'
        const pct = s.progressPct || 0
        if (pct >= 100) return 'var(--color-green)'
        if (pct >= 66) return 'var(--color-magenta)'
        if (pct >= 33) return 'var(--color-yellow)'
        return 'var(--color-cyan)'
      }
    }
  }
}

export const TriageLabelChip = {
  tag: 'span',
  padding: 'V Z',
  round: 'C',
  fontSize: 'V',
  fontWeight: (el, s) => s._more ? '700' : '500',
  letterSpacing: '.02em',
  color: (el, s) => s._more ? 'caption' : 'subtitle',
  background: 'overlay',
  borderWidth: 'V',
  borderColor: 'line',
  borderStyle: 'solid',
  text: (el, s) => s.label
}

// Blocker chip — renders a single "blocked by #N" reference inside a TriageCard.
// Each chip is clickable and opens the blocker ticket's detail modal.
export const BlockerChip = {
  tag: 'button',
  padding: 'V Z',
  round: 'C',
  fontSize: 'V',
  fontWeight: '700',
  letterSpacing: '.02em',
  color: 'red',
  background: 'color-mix(in srgb, var(--color-red) 10%, transparent)',
  borderWidth: 'V',
  borderColor: 'color-mix(in srgb, var(--color-red) 25%, transparent)',
  borderStyle: 'solid',
  cursor: 'pointer',
  transition: 'background 0.12s ease, border-color 0.12s ease',
  ':hover': {
    background: 'color-mix(in srgb, var(--color-red) 20%, transparent)',
    borderColor: 'red'
  },
  title: (el, s) => s.title ? `Blocked by: ${s.title}` : `Blocked by ticket #${s.id}`,
  text: (el, s) => s.external_id ? `#${s.external_id}` : `#${s.id}`,

  onClick: (e, el, s) => {
    e.stopPropagation()
    const root = s.root || el.getRoot()
    if (!root || typeof root.update !== 'function') return
    // Open the blocker ticket's detail modal.
    root.update({ selectedTicket: { id: s.id, external_id: s.external_id, title: s.title } })
  }
}

// ─── Ready-to-test rail ───────────────────────────────────────────────────────

export const ReadyToTestRail = {
  flow: 'y',
  gap: 'A',
  padding: 'A',
  round: 'A',
  background: 'overlay',
  borderWidth: 'V',
  borderStyle: 'solid',
  borderColor: 'line',
  overflow: 'auto',
  // Wide viewport (>=1280px): sticky + capped height so the rail tracks scroll.
  // Implemented via reactive JS prop (same workaround as Layout grid — see comment there).
  position: (el, s) =>
    (s.root.viewportWidth || (typeof window !== 'undefined' ? window.innerWidth : 0)) >= 1280
      ? 'sticky' : 'static',
  top: (el, s) =>
    (s.root.viewportWidth || (typeof window !== 'undefined' ? window.innerWidth : 0)) >= 1280
      ? 'var(--spacing-A)' : 'auto',
  maxHeight: (el, s) =>
    (s.root.viewportWidth || (typeof window !== 'undefined' ? window.innerWidth : 0)) >= 1280
      ? 'calc(50vh - 80px)' : 'none',

  Header: {
    flow: 'x',
    gap: 'Z',
    align: 'center left',
    paddingBottom: 'Z',
    marginBottom: 'V',
    borderBottomWidth: 'V',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',

    Dot: {
      tag: 'span',
      width: 'Z',
      height: 'Z',
      round: 'round',
      flexShrink: '0',
      alignSelf: 'center',
      background: 'magenta',
      boxShadow: '0 0 10px color-mix(in srgb, var(--color-magenta) 55%, transparent)'
    },
    Title: {
      tag: 'h2',
      fontSize: 'Z2',
      fontWeight: '700',
      letterSpacing: '-.01em',
      color: 'title',
      margin: '0',
      lineHeight: '1.2',
      text: 'Ready to test'
    },
    Count: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      padding: 'V Y',
      round: 'C',
      marginLeft: 'auto',
      display: 'inline-block',
      transformOrigin: 'center center',
      color: 'magenta',
      background: 'color-mix(in srgb, var(--color-magenta) 12%, transparent)',
      text: (el, s) => {
        const n = _readyToTestRows(s).length
        if (typeof el._prevCount === 'number' && el._prevCount !== n && el.node) {
          const prefersReduced = typeof window !== 'undefined' &&
            window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
          const animName = prefersReduced ? 'countBumpReduced' : 'countBump'
          el.node.style.animation = 'none'
          void el.node.offsetWidth
          el.node.style.animation = `${animName} 0.3s ease`
          el.node.addEventListener('animationend', () => {
            if (el.node) el.node.style.animation = ''
          }, { once: true })
        }
        el._prevCount = n
        return `${n}`
      }
    }
  },

  Hint: {
    hide: (el, s) => _readyToTestRows(s).length > 0,
    tag: 'p',
    margin: '0',
    padding: 'Z',
    textAlign: 'center',
    fontSize: 'Y2',
    color: 'caption',
    fontStyle: 'italic',
    text: 'No tickets ready to test'
  },

  List: {
    flow: 'y',
    gap: 'V',

    childExtends: 'TriageCard',
    childrenAs: 'state',
    children: (el, s) => _readyToTestRows(s)
  }
}

export const ReadyToTestRow = {
  flow: 'x',
  align: 'center left',
  gap: 'V',
  padding: 'V A',
  background: 'magenta.08',
  round: 'V',
  borderWidth: 'V',
  borderStyle: 'solid',
  borderColor: 'magenta.a+20',
  cursor: 'pointer',
  transition: 'border-color 0.12s ease, transform 0.12s ease',
  ':hover': { borderColor: 'magenta', transform: 'translateX(2px)' },

  onClick: (e, el, s) => {
    const root = s.root || el.getRoot()
    if (root && typeof root.update === 'function') root.update({ selectedTicket: { ...s } })
  },

  Ref: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    fontWeight: '600',
    color: 'magenta',
    fontFamily: 'monospace',
    text: (el, s) => s.external_id || `#${s.id}`
  },
  Title: {
    tag: 'span',
    flex: '1',
    fontSize: 'Y1',
    color: 'title',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => (s.title || '').replace(/^B-\d+\s*·\s*/, '')
  },
  EpicChip: {
    hide: (el, s) => !s.parentEpicTitle,
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    fontWeight: '600',
    padding: 'V Z',
    round: 'V',
    background: 'cyan.15',
    color: 'cyan',
    text: (el, s) => s.parentEpicTitle || ''
  },
  Priority: {
    hide: (el, s) => !s.priority || s.priority === 'P2',
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    fontWeight: '700',
    color: (el, s) => s.priority === 'P0' ? 'red' : s.priority === 'P1' ? 'orange' : 'caption',
    text: (el, s) => s.priority || ''
  },
  StateChip: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    fontWeight: '600',
    color: 'magenta',
    text: 'ready to test'
  }
}

// ─── Layer 4: Agent Pulse Rail ────────────────────────────────────────────────
//
// Live presence section. Subscribes to realtime via page's subscription.
// Active (<5min): pulsing green dot.
// Idle (5–60min): collapsed section.
// Stale (>60min): hidden behind "Show stale" toggle.

export const AgentPulseRail = {
  flow: 'y',
  gap: 'A',
  padding: 'A',
  round: 'A',
  // Stronger background for the rail — distinguishes it from cards
  background: (el, s) => _pulsingAgents(s).length > 0
    ? 'color-mix(in srgb, var(--color-green) 4%, var(--color-overlay))'
    : 'overlay',
  borderWidth: 'V',
  borderStyle: 'solid',
  borderColor: (el, s) => _pulsingAgents(s).length > 0 ? 'color-mix(in srgb, var(--color-green) 20%, transparent)' : 'line',
  maxHeight: 'calc(50vh - 80px)',
  overflow: 'auto',
  transition: 'background 0.3s ease, border-color 0.3s ease',

  Header: {
    flow: 'x',
    gap: 'Z',
    align: 'center left',
    paddingBottom: 'Z',
    marginBottom: 'V',
    borderBottomWidth: 'V',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',

    // Pulsing dot — token-sized (Z ≈ 8px), animated for live presence
    PulsingDot: {
      tag: 'span',
      width: 'Z',
      height: 'Z',
      round: 'round',
      flexShrink: '0',
      alignSelf: 'center',
      background: 'green',
      boxShadow: '0 0 10px color-mix(in srgb, var(--color-green) 60%, transparent)',
      animationDuration: '1.6s',
      animationTimingFunction: 'ease-in-out',
      animationIterationCount: 'infinite',
      animationFillMode: 'none',
      '@media (prefers-reduced-motion: no-preference)': { animationName: 'agentDotPulse' },
      '@media (prefers-reduced-motion: reduce)': { animationName: 'agentDotFade' }
    },
    Title: {
      tag: 'h2',
      fontSize: 'Z2',
      fontWeight: '700',
      letterSpacing: '-.01em',
      color: 'title',
      margin: '0',
      lineHeight: '1.2',
      text: 'Agents pulsing now'
    },
    // Count as a tinted green chip when agents are active
    Count: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      padding: 'V Y',
      round: 'C',
      marginLeft: 'auto',
      color: (el, s) => _pulsingAgents(s).length > 0 ? 'green' : 'caption',
      background: (el, s) => _pulsingAgents(s).length > 0
        ? 'color-mix(in srgb, var(--color-green) 15%, transparent)'
        : 'overlay',
      text: (el, s) => `${_pulsingAgents(s).length}`
    }
  },

  ActiveList: {
    flow: 'y',
    gap: 'V',

    EmptyNote: {
      hide: (el, s) => _pulsingAgents(s).length > 0,
      tag: 'p',
      margin: '0',
      padding: 'V 0',
      fontSize: 'Y2',
      color: 'caption',
      fontStyle: 'italic',
      text: 'No agents pulsing in the last 5 minutes'
    },

    Agents: {
      flow: 'y',
      gap: 'V',

      childExtends: 'AgentPulseRow',
      childrenAs: 'state',
      children: (el, s) => _pulsingAgents(s)
    }
  },

  // Idle section (5–60min)
  IdleSection: {
    flow: 'y',
    gap: 'V',
    hide: (el, s) => _idleAgents(s).length === 0,

    IdleHeader: {
      flow: 'x',
      gap: 'V',
      align: 'center left',
      cursor: 'pointer',
      padding: 'V 0',
      borderTopWidth: 'V',
      borderTopStyle: 'solid',
      borderTopColor: 'line',
      onClick: (e, el, s) => {
        const root = s.root || el.getRoot()
        if (root) root.update({ _idleSectionOpen: !root._idleSectionOpen })
      },

      Caret: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        transition: 'transform 0.12s ease',
        transform: (el, s) => s.root._idleSectionOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
        text: '▾'
      },
      Label: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '600',
        color: 'caption',
        text: (el, s) => `Idle (${_idleAgents(s).length})`
      }
    },

    IdleAgents: {
      flow: 'y',
      gap: 'V',
      hide: (el, s) => !s.root._idleSectionOpen,

      childExtends: 'AgentIdleRow',
      childrenAs: 'state',
      children: (el, s) => _idleAgents(s)
    }
  },

  // Stale section (>60min)
  StaleSection: {
    flow: 'y',
    gap: 'V',
    hide: (el, s) => _staleAgents(s).length === 0,

    StaleToggle: {
      tag: 'button',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      padding: 'V 0',
      fontSize: 'Y2',
      color: 'caption',
      textAlign: 'left',
      borderTopWidth: 'V',
      borderTopStyle: 'solid',
      borderTopColor: 'line',
      text: (el, s) => {
        const n = _staleAgents(s).length
        return s.root._staleSectionOpen ? `Hide stale (${n})` : `Show stale (${n})`
      },
      onClick: (e, el, s) => {
        const root = s.root || el.getRoot()
        if (root) root.update({ _staleSectionOpen: !root._staleSectionOpen })
      }
    },

    StaleAgents: {
      flow: 'y',
      gap: 'V',
      hide: (el, s) => !s.root._staleSectionOpen,

      childExtends: 'AgentIdleRow',
      childrenAs: 'state',
      children: (el, s) => _staleAgents(s)
    }
  }
}

// Active agent row — pulsing green badge
export const AgentPulseRow = {
  flow: 'y',
  gap: 'Y',
  padding: 'Z',
  round: 'V',
  background: 'color-mix(in srgb, var(--color-green) 4%, transparent)',
  borderWidth: 'V',
  borderStyle: 'solid',
  borderColor: 'color-mix(in srgb, var(--color-green) 15%, transparent)',
  transition: 'background 0.12s ease',
  ':hover': { background: 'color-mix(in srgb, var(--color-green) 8%, transparent)' },

  TopLine: {
    flow: 'x',
    gap: 'Z',
    align: 'center left',

    AvatarWrap: {
      position: 'relative',
      flexShrink: '0',
      width: 'C',
      height: 'C',

      AgentAvatar: {
        tag: 'span',
        width: 'C',
        height: 'C',
        round: 'round',
        align: 'center center',
        fontSize: 'Y1',
        fontWeight: '700',
        color: 'green',
        background: 'color-mix(in srgb, var(--color-green) 15%, transparent)',
        text: (el, s) => (s.agent_name || '?').slice(0, 1)
      },

      PulseDot: {
        position: 'absolute',
        bottom: '0',
        right: '0',
        width: 'Z',
        height: 'Z',
        round: 'round',
        background: 'green',
        borderWidth: 'V',
        borderStyle: 'solid',
        borderColor: 'overlay',
        animationDuration: '1.4s',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite',
        animationFillMode: 'none',
        '@media (prefers-reduced-motion: no-preference)': { animationName: 'agentDotPulse' },
        '@media (prefers-reduced-motion: reduce)': { animationName: 'agentDotFade' }
      }
    },

    Info: {
      flow: 'y',
      gap: 'Y',
      flex: '1',
      minWidth: '0',

      Name: {
        tag: 'span',
        fontSize: 'Y1',
        fontWeight: '700',
        color: 'green',
        letterSpacing: '.02em',
        text: (el, s) => s.agent_name || '?'
      },

      Status: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        text: (el, s) => {
          if (s.current_ticket_id) return `shipping ticket #${s.current_ticket_id}`
          if (s.lastActivity && s.lastActivity.kind === 'picked_up') {
            return `picked up #${s.lastActivity.ticket_id} · ${_relativeTime(s.lastActivity.created_at)}`
          }
          return 'active'
        }
      }
    },

    HeartbeatAge: {
      tag: 'span',
      flexShrink: '0',
      fontSize: 'Y2',
      color: 'caption',
      title: (el, s) => s.last_heartbeat || '',
      text: (el, s) => _relativeTime(s.last_heartbeat)
    }
  },

  // "Last shipped" sub-line
  LastShipped: {
    display: (el, s) => (s.lastActivity && s.lastActivity.kind === 'shipped') ? 'flex' : 'none',
    flow: 'x',
    gap: 'V',
    align: 'center left',
    paddingLeft: 'calc(var(--spacing-C) + var(--spacing-Z))',
    fontSize: 'Y2',
    color: 'caption',

    Icon: { tag: 'span', text: '▸', flexShrink: '0' },
    Text: {
      tag: 'span',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => {
        const a = s.lastActivity
        if (!a) return ''
        return `shipped #${a.ticket_id} · ${_relativeTime(a.created_at)}`
      }
    }
  }
}

// Idle / stale agent row
export const AgentIdleRow = {
  flow: 'x',
  gap: 'Z',
  align: 'center left',
  padding: 'V Z',

  Avatar: {
    tag: 'span',
    width: 'C',
    height: 'C',
    round: 'round',
    flexShrink: '0',
    align: 'center center',
    fontSize: 'Y2',
    fontWeight: '700',
    color: 'caption',
    background: 'line',
    text: (el, s) => (s.agent_name || '?').slice(0, 1)
  },
  Name: {
    tag: 'span',
    fontSize: 'Y2',
    fontWeight: '600',
    color: 'caption',
    flex: '1',
    text: (el, s) => s.agent_name || '?'
  },
  Age: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    opacity: '.6',
    title: (el, s) => s.last_heartbeat || '',
    text: (el, s) => _relativeTime(s.last_heartbeat)
  }
}

// ─── Blocked section ──────────────────────────────────────────────────────────
//
// Dedicated bucket between Ongoing and Upcoming.
// Renders TriageCard for each blocked ticket; blocker info appears as
// a BlockerInfo sub-row inside each card (via s.blockedByIds).
// Red left-border accent signals that action is needed.

export const BlockedSection = {
  flow: 'y',
  gap: 'A',

  hide: (el, s) => _blockedRows(s).length === 0,

  // Red left-border accent — visually distinct from ongoing (yellow) and upcoming (cyan)
  borderLeftWidth: 'V',
  borderLeftStyle: 'solid',
  borderLeftColor: 'red',
  paddingLeft: 'A',

  Header: {
    flow: 'x',
    gap: 'Z',
    align: 'center left',
    paddingBottom: 'Z',
    marginBottom: 'V',
    borderBottomWidth: 'V',
    borderBottomStyle: 'solid',
    borderBottomColor: 'color-mix(in srgb, var(--color-red) 20%, transparent)',

    Dot: {
      tag: 'span',
      width: 'Z',
      height: 'Z',
      round: 'round',
      flexShrink: '0',
      alignSelf: 'center',
      background: 'red',
      boxShadow: '0 0 10px color-mix(in srgb, var(--color-red) 55%, transparent)'
    },
    Title: {
      tag: 'h2',
      fontSize: 'A',
      fontWeight: '700',
      letterSpacing: '-.02em',
      color: 'red',
      margin: '0',
      lineHeight: '1.2',
      text: 'Blocked'
    },
    Count: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      padding: 'V Y',
      round: 'C',
      color: 'red',
      background: 'color-mix(in srgb, var(--color-red) 14%, transparent)',
      text: (el, s) => { const n = _blockedRows(s).length; return `${n}` }
    },
    Subtitle: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      fontStyle: 'italic',
      marginLeft: 'V',
      flex: '1',
      text: 'Waiting on external dependency or approval before work can resume'
    }
  },

  // Virtualized card list — uses same approach as TriageSection.
  // BlockedSection owns its own state for the visible window.
  state: { visN: 20 },
  _isTriageSection: true,

  Groups: {
    flow: 'y',
    gap: 'V',

    childExtends: 'TriageCard',
    childrenAs: 'state',
    children: (el, s) => {
      const sectionEl = el.parent
      const vis = (sectionEl.state && sectionEl.state.visN) || 20
      return _blockedRows(s).slice(0, vis)
    }
  },

  Footer: {
    flow: 'y',
    gap: 'V',
    hide: (el, s) => {
      const sectionEl = el.parent
      const vis = (sectionEl.state && sectionEl.state.visN) || 20
      return _blockedRows(s).length <= vis
    },
    Sentinel: { extends: 'VirtualSentinel' },
    MoreBtn: {
      tag: 'button',
      padding: 'V A',
      round: 'Y2',
      border: 'none',
      cursor: 'pointer',
      fontSize: 'Y2',
      fontWeight: '600',
      background: 'overlay',
      color: 'caption',
      borderWidth: 'V',
      borderStyle: 'solid',
      borderColor: 'line',
      transition: 'border-color 0.12s ease, color 0.12s ease',
      ':hover': { borderColor: 'red', color: 'red' },
      text: (el, s) => {
        const sectionEl = el.parent.parent
        const total = _blockedRows(s).length
        const vis = (sectionEl.state && sectionEl.state.visN) || 20
        const remaining = Math.max(0, total - vis)
        return `Load ${Math.min(remaining, 20)} more (${remaining} remaining)`
      },
      onClick: (e, el, s) => {
        const sectionEl = _findTriageSection(el)
        _expandSection(sectionEl)
      }
    }
  }
}

// ─── Pending Approvals section ────────────────────────────────────────────────

export const PendingApprovalsSection = {
  flow: 'y',
  gap: 'A',

  hide: (el, s) => _pendingApprovalRows(s).length === 0,

  // Amber left-border accent — action-needed urgency signal
  borderLeftWidth: 'Z',
  borderLeftStyle: 'solid',
  borderLeftColor: 'orange',
  paddingLeft: 'A',
  round: 'V',
  background: 'color-mix(in srgb, var(--color-orange) 3%, transparent)',

  Header: {
    flow: 'x',
    gap: 'Z',
    align: 'center left',
    paddingBottom: 'Z',
    marginBottom: 'V',
    borderBottomWidth: 'V',
    borderBottomStyle: 'solid',
    borderBottomColor: 'color-mix(in srgb, var(--color-orange) 20%, transparent)',

    // Amber dot — token-sized
    Dot: {
      tag: 'span',
      width: 'Z',
      height: 'Z',
      round: 'round',
      flexShrink: '0',
      alignSelf: 'center',
      background: 'orange',
      boxShadow: '0 0 10px color-mix(in srgb, var(--color-orange) 55%, transparent)'
    },
    Title: {
      tag: 'h2',
      fontSize: 'A',
      fontWeight: '700',
      letterSpacing: '-.02em',
      color: 'orange',
      margin: '0',
      lineHeight: '1.2',
      text: 'Pending approvals'
    },
    // Count chip in amber
    Count: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      padding: 'V Y',
      round: 'C',
      color: 'orange',
      background: 'color-mix(in srgb, var(--color-orange) 15%, transparent)',
      text: (el, s) => { const n = _pendingApprovalRows(s).length; return `${n}` }
    },
    Subtitle: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      fontStyle: 'italic',
      marginLeft: 'V',
      flex: '1',
      text: 'Waiting for human approval before agents can proceed'
    }
  },

  List: {
    flow: 'y',
    gap: 'V',

    childExtends: 'ApprovalRow',
    childrenAs: 'state',
    children: (el, s) => _pendingApprovalRows(s)
  }
}

export const ApprovalRow = {
  flow: 'x',
  align: 'center left',
  gap: 'V',
  padding: 'V A',
  background: (el, s) => _isHumanApprovalNeeded(s) ? 'orange.08' : 'overlay',
  round: 'V',
  borderWidth: 'V',
  borderStyle: 'solid',
  borderColor: (el, s) => _isHumanApprovalNeeded(s) ? 'orange.a+30' : 'line',
  transition: 'border-color 0.12s ease',
  ':hover': { borderColor: 'orange' },
  cursor: 'pointer',

  onClick: (e, el, s) => {
    if (e.target?.dataset?.approveBtn) return
    const root = s.root || el.getRoot()
    if (root && typeof root.update === 'function') root.update({ selectedTicket: { ...s } })
  },

  LockIcon: {
    hide: (el, s) => !_isHumanApprovalNeeded(s),
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y1',
    color: 'orange',
    text: '🔒',
    title: 'Requires human approval before agents can proceed'
  },

  Ref: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    fontWeight: '600',
    color: 'orange',
    fontFamily: 'monospace',
    text: (el, s) => s.external_id || `#${s.id}`
  },

  Title: {
    tag: 'span',
    flex: '1',
    fontSize: 'Y1',
    color: 'title',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => (s.title || '').replace(/^B-\d+\s*·\s*/, '')
  },

  Priority: {
    hide: (el, s) => !s.priority || s.priority === 'P2',
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    fontWeight: '700',
    color: (el, s) => s.priority === 'P0' ? 'red' : s.priority === 'P1' ? 'orange' : 'caption',
    text: (el, s) => s.priority || ''
  },

  Assignee: {
    hide: (el, s) => !s.assignee_email,
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) => {
      const e = s.assignee_email || ''
      if (_isAgentEmail(e)) return e.split('-agent')[0].toUpperCase()
      return e.split('@')[0]
    }
  },

  ApproveBtn: {
    tag: 'button',
    flexShrink: '0',
    padding: 'V Z',
    round: 'Y2',
    border: 'none',
    cursor: 'pointer',
    fontSize: 'Y2',
    fontWeight: '700',
    background: 'orange.15',
    color: 'orange',
    transition: 'background 0.12s ease, color 0.12s ease',
    ':hover': { background: 'orange', color: 'white' },
    text: 'Approve',
    'data-approve-btn': 'true',
    onClick: async (e, el, s) => {
      e.stopPropagation()
      const id = s.id
      if (!id) return
      const root = s.root || el.getRoot()
      if (root && root.triage) {
        const next = { ...(root.triage), pendingApproval: (root.triage.pendingApproval || []).filter(t => t.id !== id) }
        root.update({ triage: next })
      }
      await _approveTicket(id)
    }
  }
}

// ─── Decisions section ────────────────────────────────────────────────────────
//
// Surfaces type=decision tickets (ASK-USER / pending-decision items) in a
// dedicated purple-accented section so they are never invisible on the page.
// Previously these tickets were excluded from all buckets by the
// `rType !== 'task' && rType !== 'bug'` guard in computeTriage (ticket #4637).

export const DecisionsSection = {
  flow: 'y',
  gap: 'A',

  hide: (el, s) => _decisionRows(s).length === 0,

  // Purple left-border accent — decision / ASK-USER signal
  borderLeftWidth: 'Z',
  borderLeftStyle: 'solid',
  borderLeftColor: 'purple',
  paddingLeft: 'A',
  round: 'V',
  background: 'color-mix(in srgb, var(--color-purple) 3%, transparent)',

  Header: {
    flow: 'x',
    gap: 'Z',
    align: 'center left',
    paddingBottom: 'Z',
    marginBottom: 'V',
    borderBottomWidth: 'V',
    borderBottomStyle: 'solid',
    borderBottomColor: 'color-mix(in srgb, var(--color-purple) 20%, transparent)',

    Dot: {
      tag: 'span',
      width: 'Z',
      height: 'Z',
      round: 'round',
      flexShrink: '0',
      alignSelf: 'center',
      background: 'purple',
      boxShadow: '0 0 10px color-mix(in srgb, var(--color-purple) 55%, transparent)'
    },
    Title: {
      tag: 'h2',
      fontSize: 'A',
      fontWeight: '700',
      letterSpacing: '-.02em',
      color: 'purple',
      margin: '0',
      lineHeight: '1.2',
      text: 'Decisions needed'
    },
    Count: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      padding: 'V Y',
      round: 'C',
      color: 'purple',
      background: 'color-mix(in srgb, var(--color-purple) 15%, transparent)',
      text: (el, s) => { const n = _decisionRows(s).length; return `${n}` }
    },
    Subtitle: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      fontStyle: 'italic',
      marginLeft: 'V',
      flex: '1',
      text: 'ASK-USER tickets awaiting a decision before agents can proceed'
    }
  },

  List: {
    flow: 'y',
    gap: 'V',

    childExtends: 'DecisionRow',
    childrenAs: 'state',
    children: (el, s) => _decisionRows(s)
  }
}

export const DecisionRow = {
  flow: 'x',
  align: 'center left',
  gap: 'V',
  padding: 'V A',
  background: 'overlay',
  round: 'V',
  borderWidth: 'V',
  borderStyle: 'solid',
  borderColor: 'line',
  transition: 'border-color 0.12s ease',
  ':hover': { borderColor: 'purple' },
  cursor: 'pointer',

  onClick: (e, el, s) => {
    const root = s.root || el.getRoot()
    if (root && typeof root.update === 'function') root.update({ selectedTicket: { ...s } })
  },

  DecisionIcon: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y1',
    color: 'purple',
    text: '?',
    title: 'Decision ticket — awaiting user input'
  },

  Ref: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    fontWeight: '600',
    color: 'purple',
    fontFamily: 'monospace',
    text: (el, s) => s.external_id || `#${s.id}`
  },

  Title: {
    tag: 'span',
    flex: '1',
    fontSize: 'Y1',
    color: 'title',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => (s.title || '').replace(/^[^\s·]+\s*·\s*/, '')
  },

  Priority: {
    hide: (el, s) => !s.priority || s.priority === 'P2',
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    fontWeight: '700',
    color: (el, s) => s.priority === 'P0' ? 'red' : s.priority === 'P1' ? 'orange' : 'caption',
    text: (el, s) => s.priority || ''
  },

  Assignee: {
    hide: (el, s) => !s.assignee_email,
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) => {
      const e = s.assignee_email || ''
      if (_isAgentEmail(e)) return e.split('-agent')[0].toUpperCase()
      return e.split('@')[0]
    }
  },

  Labels: {
    hide: (el, s) => !(s.labels && s.labels.length),
    flow: 'x',
    gap: 'Y',
    flexWrap: 'wrap',
    flexShrink: '0',
    children: (el, s) => (s.labels || []).filter(l => l === 'ASK-USER').map(l => ({
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      padding: 'V Y',
      round: 'C',
      background: 'color-mix(in srgb, var(--color-purple) 15%, transparent)',
      color: 'purple',
      text: l
    }))
  }
}

// ─── Shared backward-compat FilterChip ───────────────────────────────────────

export const FilterChip = {
  tag: 'button',
  padding: 'Y Z',
  round: 'Z',
  fontSize: 'Y2',
  fontWeight: (el, s) => s.active ? '700' : '500',
  cursor: 'pointer',
  borderWidth: 'V',
  borderStyle: 'solid',
  // Active: solid cyan border + strong tinted background + bold
  // Inactive: subtle line border + transparent bg — maximum contrast delta
  borderColor: (el, s) => s.active ? 'cyan' : 'line',
  background: (el, s) => s.active ? 'cyan.15' : 'overlay',
  color: (el, s) => s.active ? 'cyan' : 'caption',
  transition: 'all 0.12s ease',
  boxShadow: (el, s) => s.active ? '0 0 0 2px color-mix(in srgb, var(--color-cyan) 15%, transparent)' : 'none',
  ':hover': {
    borderColor: (el, s) => s.active ? 'cyan' : 'title',
    color: (el, s) => s.active ? 'cyan' : 'title'
  },

  text: (el, s) => s.label || s.key,

  onClick: (e, el, s) => {
    const root = s.root || el.getRoot()
    if (root && typeof root.update === 'function') {
      const key = s.filterKey || 'filterAssignee'
      root.update({ [key]: s.key })
    }
  }
}

// ─── AC12: Density toggle ─────────────────────────────────────────────────────
//
// Compact / Comfortable segmented toggle. Compact halves card vertical padding.
// Token-based only: no raw px.

export const TriageDensityToggle = {
  flow: 'x',
  align: 'center right',
  gap: 'Y',
  flexShrink: '0',

  Label: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    fontWeight: '500',
    text: 'View'
  },

  SegGroup: {
    flow: 'x',
    round: 'Z',
    overflow: 'hidden',
    borderWidth: 'V',
    borderStyle: 'solid',
    borderColor: 'line',

    Compact: {
      tag: 'button',
      padding: 'V Z',
      fontSize: 'Y2',
      fontWeight: (el, s) => s.root.densityMode === 'compact' ? '700' : '500',
      cursor: 'pointer',
      border: 'none',
      borderRightWidth: 'V',
      borderRightStyle: 'solid',
      borderRightColor: 'line',
      background: (el, s) => s.root.densityMode === 'compact' ? 'cyan.15' : 'transparent',
      color: (el, s) => s.root.densityMode === 'compact' ? 'cyan' : 'caption',
      transition: 'background 0.12s ease, color 0.12s ease',
      ':hover': {
        background: (el, s) => s.root.densityMode === 'compact' ? 'cyan.15' : 'overlay',
        color: (el, s) => s.root.densityMode === 'compact' ? 'cyan' : 'title'
      },
      title: 'Compact view — tighter card padding',
      text: 'Compact',
      onClick: (e, el, s) => {
        const root = s.root || el.getRoot()
        if (root) root.update({ densityMode: 'compact' })
      }
    },

    Comfortable: {
      tag: 'button',
      padding: 'V Z',
      fontSize: 'Y2',
      fontWeight: (el, s) => (s.root.densityMode || 'comfortable') === 'comfortable' ? '700' : '500',
      cursor: 'pointer',
      border: 'none',
      background: (el, s) => (s.root.densityMode || 'comfortable') === 'comfortable' ? 'cyan.15' : 'transparent',
      color: (el, s) => (s.root.densityMode || 'comfortable') === 'comfortable' ? 'cyan' : 'caption',
      transition: 'background 0.12s ease, color 0.12s ease',
      ':hover': {
        background: (el, s) => (s.root.densityMode || 'comfortable') === 'comfortable' ? 'cyan.15' : 'overlay',
        color: (el, s) => (s.root.densityMode || 'comfortable') === 'comfortable' ? 'cyan' : 'title'
      },
      title: 'Comfortable view — standard card padding',
      text: 'Comfortable',
      onClick: (e, el, s) => {
        const root = s.root || el.getRoot()
        if (root) root.update({ densityMode: 'comfortable' })
      }
    }
  }
}

// ─── AC11: Quick-create input ─────────────────────────────────────────────────
//
// Pressing 'c' anywhere on the page focuses this inline input.
// Enter key creates a ticket in the active bucket (now = in_progress state).
// Esc clears and blurs.

export const TriageQuickCreate = {
  flow: 'x',
  gap: 'Z',
  align: 'center left',
  padding: 'Z A',
  round: 'Z1',
  background: 'overlay',
  borderWidth: 'V',
  borderStyle: 'solid',
  borderColor: 'line',
  transition: 'border-color 0.12s ease',

  onInit: (el) => {
    // Store reference for global 'c' key handler to find
    const input = el.node && el.node.querySelector('[data-quick-create-input]')
    if (input) el._quickCreateInput = input
  },

  Icon: {
    tag: 'span',
    fontSize: 'Y1',
    color: 'caption',
    flexShrink: '0',
    text: '+'
  },

  Input: {
    tag: 'input',
    flex: '1',
    border: 'none',
    background: 'transparent',
    fontSize: 'Y1',
    color: 'title',
    outline: 'none',
    placeholder: 'Quick-create ticket in active bucket… (press c)',
    'data-quick-create-input': 'true',

    onFocus: (e, el, s) => {
      const parent = el.parent
      if (parent && parent.node) {
        parent.node.style.borderColor = 'var(--color-cyan)'
      }
    },

    onBlur: (e, el, s) => {
      const parent = el.parent
      if (parent && parent.node) {
        parent.node.style.borderColor = ''
      }
    },

    onKeydown: async (e, el, s) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        if (el.node) { el.node.value = ''; el.node.blur() }
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        const title = el.node ? el.node.value.trim() : ''
        if (!title) return

        // Determine target bucket state from active kbFocusBucket or 'now'
        const root = s.root || el.getRoot()
        const bk = (root && root.kbFocusBucket) || 'now'
        const stateMap = {
          now: 'in_progress',
          ongoing: 'in_progress',
          upcoming: 'not_started',
          pendingApproval: 'awaiting_approval'
        }
        const ticketState = stateMap[bk] || 'not_started'

        try {
          await (el.sdk || getSDK()).execute('workspaceProject.tickets', 'create', {
            payload: { title, state: ticketState, type: 'task' }
          })
          if (el.node) { el.node.value = ''; el.node.blur() }
          // Trigger store refresh so new ticket appears in the bucket.
          ticketStore.refresh().catch(() => {})
        } catch (err) {
          console.error('[QuickCreate]', err)
        }
      }
    }
  },

  Hint: {
    tag: 'kbd',
    fontSize: 'Y2',
    padding: 'V Y',
    round: 'Y',
    flexShrink: '0',
    color: 'caption',
    background: 'line',
    borderWidth: 'V',
    borderStyle: 'solid',
    borderColor: 'overlayStrong',
    fontFamily: 'monospace',
    title: 'Press c to focus',
    text: 'c'
  }
}

// ─── AC6: Multi-select action bar ─────────────────────────────────────────────
//
// Fixed bottom bar shown when 1+ tickets are selected via Shift/Cmd+click.
// Actions: Move to bucket / Change state / Assign / Deselect all.

export const TriageSelectionActionBar = {
  position: 'fixed',
  bottom: 'A',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: '200',
  hide: (el, s) => {
    const ids = s.root && s.root.selectedTicketIds
    return !Array.isArray(ids) || ids.length === 0
  },
  // AC6: expose selection state as DOM attributes so automated tests can
  // verify multiselect without triggering synthetic click events that hit
  // the DOMQL state boundary. Tests read:
  //   document.querySelector('[data-test-selection-bar]').dataset.testSelectedCount
  //   document.querySelector('[data-test-selection-bar]').dataset.testSelectedIds
  'data-test-selection-bar': 'true',
  'data-test-selected-count': (el, s) => {
    const ids = s.root && s.root.selectedTicketIds
    return Array.isArray(ids) && ids.length ? String(ids.length) : '0'
  },
  'data-test-selected-ids': (el, s) => {
    const ids = s.root && s.root.selectedTicketIds
    return Array.isArray(ids) && ids.length ? ids.join(',') : ''
  },
  flow: 'x',
  gap: 'Z',
  align: 'center center',
  padding: 'Z B',
  round: 'A',
  background: 'title',
  boxShadow: '0 4px 24px var(--color-shadow)',
  borderWidth: 'V',
  borderStyle: 'solid',
  borderColor: 'line',

  Count: {
    tag: 'span',
    fontSize: 'Y1',
    fontWeight: '700',
    color: 'document',
    text: (el, s) => {
      const ids = s.root && s.root.selectedTicketIds
      const n = (Array.isArray(ids) ? ids.length : 0)
      return `${n} selected`
    }
  },

  Sep_1: {
    tag: 'span',
    width: 'V',
    height: 'A',
    background: 'line',
    opacity: '.3'
  },

  MoveBtn: {
    tag: 'button',
    padding: 'Y Z',
    round: 'Z',
    border: 'none',
    cursor: 'pointer',
    fontSize: 'Y2',
    fontWeight: '600',
    background: 'transparent',
    color: 'document',
    transition: 'background 0.1s ease',
    ':hover': { background: 'color-mix(in srgb, var(--color-white) 15%, transparent)' },
    text: 'Move to…',
    onClick: (e, el, s) => {
      // Stub: future — open a bucket-picker popover
      console.log('[SelectionBar] Move:', s.root.selectedTicketIds || [])
    }
  },

  AssignBtn: {
    tag: 'button',
    padding: 'Y Z',
    round: 'Z',
    border: 'none',
    cursor: 'pointer',
    fontSize: 'Y2',
    fontWeight: '600',
    background: 'transparent',
    color: 'document',
    transition: 'background 0.1s ease',
    ':hover': { background: 'color-mix(in srgb, var(--color-white) 15%, transparent)' },
    text: 'Assign…',
    onClick: (e, el, s) => {
      console.log('[SelectionBar] Assign:', s.root.selectedTicketIds || [])
    }
  },

  Sep_2: {
    tag: 'span',
    width: 'V',
    height: 'A',
    background: 'line',
    opacity: '.3'
  },

  ClearBtn: {
    tag: 'button',
    padding: 'Y Z',
    round: 'Z',
    border: 'none',
    cursor: 'pointer',
    fontSize: 'Y2',
    fontWeight: '600',
    background: 'transparent',
    color: 'caption',
    transition: 'background 0.1s ease',
    ':hover': { background: 'color-mix(in srgb, var(--color-white) 15%, transparent)' },
    text: 'Clear selection',
    onClick: (e, el, s) => {
      const root = s.root || el.getRoot()
      if (root) root.update({ selectedTicketIds: [] })
    }
  }
}
